import React, { useEffect, useState } from 'react';
import { Card, Form, Table, Button, Row, Col } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import API from '../services/api';
import SearchSelect from './SearchSelect';
import { useClientRepricing } from '../utils/clientPrices';
import { daysFromTodayDR, formatDay } from '../utils/dates';

const money = (n) => new Intl.NumberFormat('es-DO', { style: 'currency', currency: 'DOP' }).format(n || 0);

const emptyItem = () => ({
  product: '', description: '', quantity: 1, unitPrice: 0, itbisRate: 0.18,
});

const defaultValidUntil = () => daysFromTodayDR(15); // Dominican days (toISOString is UTC: tomorrow after 8 p.m.)

// A stored line as the builder holds it (product as its id).
const itemOf = (it) => ({
  product: it.product?._id || it.product || '', sku: it.sku, description: it.description || '', quantity: it.quantity,
  unitPrice: it.unitPrice, priceEdited: true, itbisRate: it.itbisRate ?? 0.18, itbisExento: !!it.itbisExento, unidadMedida: it.unidadMedida,
});

// New (/quotes/new) or "Editar" (/quotes/:id/edit): the same form. A quote made by WhatsApp may be for someone who isn't
// a client yet (just a name, which stays until a client is chosen) and carries the proposal's texts — scope of work,
// payment terms, conditions — which are edited here too. Saving keeps its number; one already invoiced can't change.

const QuoteBuilder = () => {
  const navigate = useNavigate();
  const { id } = useParams(); // set when editing
  const [quote, setQuote] = useState(null); // the quote being edited, as loaded
  const [clientName, setClientName] = useState('');
  const [clientDetail, setClientDetail] = useState('');
  const [proposal, setProposal] = useState({ title: '', summary: '', steps: '', excludes: '', paymentTerms: '', conditions: '' });
  const [clients, setClients] = useState([]);
  const [products, setProducts] = useState([]);
  const [clientId, setClientId] = useState('');
  const [validUntil, setValidUntil] = useState(defaultValidUntil());
  const [notes, setNotes] = useState('');
  const [items, setItems] = useState([emptyItem()]);
  const [quoteExento, setQuoteExento] = useState(false);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    API.get('/clients').then(res => setClients(res.data)).catch(err => console.error(err));
    API.get('/products').then(res => setProducts(res.data)).catch(err => console.error(err));
  }, []);

  useEffect(() => {
    if (!id) return;
    API.get(`/quotes/${id}`).then(({ data }) => {
      setQuote(data);
      setClientId(data.client?._id || '');
      setClientName(data.clientName || '');
      setClientDetail(data.clientDetail || '');
      if (data.validUntil) setValidUntil(String(data.validUntil).slice(0, 10));
      setNotes(data.notes || '');
      setItems(data.items?.length ? data.items.map(itemOf) : [emptyItem()]);
      setProposal({
        title: data.scope?.title || '', summary: data.scope?.summary || '', steps: (data.scope?.steps || []).join('\n'),
        excludes: data.scope?.excludes || '', paymentTerms: data.paymentTerms || '', conditions: data.conditions || '',
      });
      if (data.status === 'accepted') setError(`La cotización ${data.number} ya se convirtió en factura: ya no se cambia.`);
    }).catch(err => setError(err.response?.data?.message || 'No se pudo cargar la cotización.'));
  }, [id]);

  const fromWhatsApp = quote?.source === 'whatsapp';
  const locked = quote?.status === 'accepted';
  const setProp = (key) => (e) => setProposal(prev => ({ ...prev, [key]: e.target.value }));

  const selectedClient = clients.find(c => c._id === clientId);

  const { priceNote, repriceFor } = useClientRepricing(items, setItems);

  const updateItem = (index, patch) => {
    setItems(prev => prev.map((it, i) => i === index ? { ...it, ...patch } : it));
  };

  const handleProductChange = async (index, productId) => {
    const product = products.find(p => p._id === productId);
    if (!product) {
      updateItem(index, { product: '' });
      return;
    }

    let unitPrice = product.price;
    if (clientId) {
      try {
        const { data } = await API.get('/client-prices/resolve', { params: { client: clientId, product: productId } });
        unitPrice = data.price;
      } catch (err) {
        console.error('Error resolving client price:', err);
      }
    }

    updateItem(index, {
      product: productId,
      sku: product.sku,
      description: product.name,
      unitPrice,
      priceEdited: false,
      itbisRate: product.itbisRate,
      unidadMedida: product.unidadMedida,
    });
  };

  const addItem = () => setItems(prev => [...prev, emptyItem()]);
  const removeItem = (index) => setItems(prev => prev.filter((_, i) => i !== index));

  // No ITBIS on a line when the whole quote is exempt or when its product is exempt (e.g. oxígeno medicinal).
  const productExento = (item) => !!products.find(p => p._id === item.product)?.itbisExento;
  const lineExento = (item) => quoteExento || productExento(item) || !!item.itbisExento;

  const totals = items.reduce((acc, item) => {
    const lineTotal = (Number(item.quantity) || 0) * (Number(item.unitPrice) || 0);
    acc.subtotal += lineTotal;
    if (!lineExento(item)) acc.itbis += lineTotal * (item.itbisRate ?? 0.18);
    return acc;
  }, { subtotal: 0, itbis: 0 });
  const grandTotal = totals.subtotal + totals.itbis;

  const handleSubmit = async () => {
    setError('');

    if (!clientId && !(fromWhatsApp && clientName.trim())) {
      setError('Selecciona un cliente.');
      return;
    }
    const validItems = items.filter(it => it.description && Number(it.quantity) > 0);
    if (validItems.length === 0) {
      setError('Agrega al menos una línea con descripción y cantidad.');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...(clientId ? { client: clientId } : { clientName: clientName.trim() }),
        validUntil,
        notes,
        ...(quote ? {} : { status: 'sent' }),
        items: validItems.map(it => ({
          product: it.product || undefined,
          sku: it.sku,
          description: it.description,
          quantity: Number(it.quantity),
          unitPrice: Number(it.unitPrice),
          itbisExento: lineExento(it),
          itbisRate: it.itbisRate,
          unidadMedida: it.unidadMedida,
        })),
      };
      if (fromWhatsApp) {
        const steps = proposal.steps.split('\n').map(st => st.trim()).filter(Boolean);
        Object.assign(payload, {
          clientDetail: clientDetail.trim(),
          scope: proposal.summary.trim() || steps.length || proposal.excludes.trim()
            ? { title: proposal.title.trim(), summary: proposal.summary.trim(), steps, excludes: proposal.excludes.trim() }
            : '',
          paymentTerms: proposal.paymentTerms.trim(),
          conditions: proposal.conditions.trim(),
        });
      }
      const { data } = quote ? await API.put(`/quotes/${quote._id}`, payload) : await API.post('/quotes', payload);
      // A WhatsApp quote's PDF opens from Cotizaciones (Ver / Imprimir); one made here, in its print view.
      if (!fromWhatsApp) window.open(`/quotes/${data._id}/print`, '_blank');
      navigate('/quotes');
    } catch (err) {
      setError(err.response?.data?.message || 'Error al guardar la cotización.');
      setSaving(false);
    }
  };

  return (
    <div className="container-fluid p-4">
      <h2 className="app-title mb-4" style={{ textAlign: 'left' }}>
        {id ? `Editar Cotización ${quote?.number || ''}` : 'Nueva Cotización'}
        {fromWhatsApp && <i className="bi bi-whatsapp ms-2" title="Hecha por WhatsApp" style={{ color: 'var(--mm-cyan)', fontSize: '0.8em' }} />}
      </h2>
      {quote && (
        <div className="text-muted small mb-3">
          Emitida el {formatDay(quote.createdAt)}. Al guardar conserva su número{fromWhatsApp ? ' y su PDF se rehace con los cambios' : ''}.
        </div>
      )}

      {error && <div className="alert alert-danger">{error}</div>}

      <Card className="shadow-sm p-3 mb-3">
        <Row>
          <Col md={7}>
            <Form.Label>Cliente</Form.Label>
            <SearchSelect
              options={clients}
              value={clientId}
              getKey={c => c._id}
              getLabel={c => `${c.name}${c.rnc ? ' — RNC ' + c.rnc : ''}`}
              onSelect={c => { setClientId(c._id); repriceFor(c._id, c.name); }}
              placeholder="Buscar cliente..."
              emptyLabel="Sin clientes coincidentes"
            />
          </Col>
          <Col md={5}>
            <Form.Label>Válida hasta</Form.Label>
            <Form.Control type="date" value={validUntil} onChange={e => setValidUntil(e.target.value)} />
          </Col>
        </Row>
        {selectedClient && (
          <div className="text-muted small mt-2">
            {selectedClient.address || 'Sin dirección registrada'} · {selectedClient.email || 'Sin email'} · {selectedClient.phone || 'Sin teléfono'}
          </div>
        )}
        {fromWhatsApp && (
          <Row className="mt-2">
            {!clientId && (
              <Col md={6}>
                <Form.Label>Nombre (sin cliente en Ventas)</Form.Label>
                <Form.Control value={clientName} onChange={e => setClientName(e.target.value)} placeholder="A quién se le cotiza" />
                <div className="text-muted small mt-1">Va solo con este nombre; elige arriba un cliente si ya está en Ventas.</div>
              </Col>
            )}
            <Col md={clientId ? 12 : 6}>
              <Form.Label>Línea bajo el nombre</Form.Label>
              <Form.Control value={clientDetail} onChange={e => setClientDetail(e.target.value)} placeholder="Ciudad, dirección, RNC o a la atención de quién (opcional)" />
            </Col>
          </Row>
        )}
        <Row className="mt-2">
          <Col md={12}>
            <Form.Label>Notas</Form.Label>
            <Form.Control as="textarea" rows={2} value={notes} onChange={e => setNotes(e.target.value)} placeholder="Notas visibles en la cotización (opcional)" />
          </Col>
        </Row>
      </Card>

      <Card className="shadow-sm p-3 mb-3">
        <Card.Title>Productos y Servicios</Card.Title>
        {priceNote && <div className="small mb-2" style={{ color: 'var(--mm-cyan)' }}>{priceNote}</div>}
        <Table responsive className="mb-2">
          <thead>
            <tr>
              <th style={{ width: 50 }}>No.</th>
              <th style={{ minWidth: 140 }}>No. de Item</th>
              <th style={{ minWidth: 180 }}>Producto</th>
              <th style={{ minWidth: 180 }}>Descripción</th>
              <th style={{ width: 90 }}>Cantidad</th>
              <th style={{ width: 130 }}>Precio Unit.</th>
              <th style={{ width: 70 }} className="text-center" title="Esta línea no lleva ITBIS">Exento</th>
              <th style={{ width: 120 }} className="text-end">Total</th>
              <th style={{ width: 40 }}></th>
            </tr>
          </thead>
          <tbody>
            {items.map((item, i) => {
              const lineTotal = (Number(item.quantity) || 0) * (Number(item.unitPrice) || 0);
              return (
                <tr key={i}>
                  <td className="align-middle text-muted">{i + 1}</td>
                  <td>
                    <SearchSelect
                      options={products}
                      value={item.product}
                      getKey={p => p._id}
                      getLabel={p => p.sku || ''}
                      onSelect={p => handleProductChange(i, p._id)}
                      placeholder="Buscar No. de Item..."
                      emptyLabel="Sin productos coincidentes"
                    />
                  </td>
                  <td>
                    <SearchSelect
                      options={products}
                      value={item.product}
                      getKey={p => p._id}
                      getLabel={p => p.name}
                      onSelect={p => handleProductChange(i, p._id)}
                      placeholder="Buscar producto..."
                      emptyLabel="Sin productos coincidentes"
                    />
                  </td>
                  <td>
                    <Form.Control
                      value={item.description}
                      onChange={e => updateItem(i, { description: e.target.value })}
                    />
                  </td>
                  <td>
                    <Form.Control
                      type="number" min="0" step="1"
                      value={item.quantity}
                      onChange={e => updateItem(i, { quantity: e.target.value })}
                    />
                  </td>
                  <td>
                    <Form.Control
                      type="number" min="0" step="0.01"
                      value={item.unitPrice}
                      onChange={e => updateItem(i, { unitPrice: e.target.value, priceEdited: true })}
                    />
                  </td>
                  <td className="text-center align-middle">
                    <Form.Check
                      checked={lineExento(item)}
                      disabled={quoteExento || productExento(item)}
                      onChange={e => updateItem(i, { itbisExento: e.target.checked })}
                    />
                  </td>
                  <td className="text-end align-middle">{money(lineTotal)}</td>
                  <td className="align-middle">
                    <Button size="sm" variant="outline-danger" onClick={() => removeItem(i)} disabled={items.length === 1}>
                      <i className="bi bi-x" />
                    </Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
        <Button size="sm" variant="outline-secondary" onClick={addItem}>+ Agregar Línea</Button>

        <div className="d-flex justify-content-end mt-4">
          <div style={{ minWidth: 260 }}>
            <div className="d-flex justify-content-end mb-3">
              <Form.Check
                label="Cotización exenta de ITBIS"
                checked={quoteExento}
                onChange={e => setQuoteExento(e.target.checked)}
              />
            </div>
            <div className="small text-muted mb-3 text-end">Marcarla exime todas las líneas. Los productos exentos (ej.: oxígeno medicinal) nunca llevan ITBIS, aunque esté desmarcada.</div>
            <div className="d-flex justify-content-between mb-1">
              <span className="text-muted">Subtotal</span>
              <span>{money(totals.subtotal)}</span>
            </div>
            <div className="d-flex justify-content-between mb-2">
              <span className="text-muted">ITBIS</span>
              <span>{money(totals.itbis)}</span>
            </div>
            <hr style={{ borderColor: 'var(--mm-line)' }} />
            <div className="d-flex justify-content-between">
              <span style={{ fontWeight: 700, fontFamily: "'DM Sans', sans-serif" }}>Total</span>
              <span style={{ fontWeight: 700, fontSize: '1.3rem', color: 'var(--mm-cyan)' }}>{money(grandTotal)}</span>
            </div>
          </div>
        </div>
      </Card>

      {fromWhatsApp && (
        <Card className="shadow-sm p-3 mb-3">
          <Card.Title>Propuesta</Card.Title>
          <Row>
            <Col md={4}>
              <Form.Label>Título del alcance</Form.Label>
              <Form.Control value={proposal.title} onChange={setProp('title')} placeholder="Alcance del trabajo" />
            </Col>
            <Col md={8}>
              <Form.Label>Resumen</Form.Label>
              <Form.Control as="textarea" rows={2} value={proposal.summary} onChange={setProp('summary')} placeholder="Qué trabajo se hará y dónde" />
            </Col>
          </Row>
          <Form.Label className="mt-2">Pasos (uno por línea; se numeran solos)</Form.Label>
          <Form.Control as="textarea" rows={Math.min(12, Math.max(3, proposal.steps.split('\n').length + 1))} value={proposal.steps} onChange={setProp('steps')} />
          <Form.Label className="mt-2">No incluye</Form.Label>
          <Form.Control value={proposal.excludes} onChange={setProp('excludes')} placeholder="Lo que no incluye (opcional)" />
          <Row className="mt-2">
            <Col md={5}>
              <Form.Label>Forma de pago</Form.Label>
              <Form.Control value={proposal.paymentTerms} onChange={setProp('paymentTerms')} placeholder="a convenir" />
            </Col>
            <Col md={7}>
              <Form.Label>Otra condición</Form.Label>
              <Form.Control value={proposal.conditions} onChange={setProp('conditions')} placeholder="Tiempo de entrega, garantía… (opcional)" />
            </Col>
          </Row>
          <div className="text-muted small mt-2">La validez, la moneda y el ITBIS se escriben solos en las condiciones.</div>
        </Card>
      )}

      <div className="d-flex justify-content-end gap-2">
        <Button variant="outline-secondary" onClick={() => navigate('/quotes')}>Cancelar</Button>
        <Button onClick={handleSubmit} disabled={saving || locked || (id && !quote)}>
          {saving ? 'Guardando...' : quote ? 'Guardar Cambios' : 'Publicar Cotización'}
        </Button>
      </div>
    </div>
  );
};

export default QuoteBuilder;
